




















































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import {
  useUpdateAction,
  useCreateAction
} from '@/composable/useWorkflowTransitionAction';
import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IWorkflowTransitionAction,
  IWorkflowTransitionActionData,
  IWorkflowTransitionActionEmailRecipientData,
  TransitionActionRecipientSubTypeEnum,
  TransitionActionRecipientTypeEnum,
  WorkflowTransitionAction,
  WorkflowTransitionActionTypeEnum
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import {
  IWorkflowStoreState,
  IWorkflowGetters,
  IWorkflowActions,
  IWorkflowMutations
} from '@/store/workflow';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowTransitionActionEmailRecipientsAutoCompletePicker from './WorkflowTransitionActionEmailRecipientsAutoCompletePicker .vue';

export enum WizardSteps {
  ActionBaseForm,
  ActionTypeForm
}

export default defineComponent({
  name: 'WorkflowTransitionActionFormDialog',
  components: {
    ALDialog,
    ErrorAlert,
    GroupAutoCompletePicker,
    WorkflowTransitionActionEmailRecipientsAutoCompletePicker
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    action: {
      type: Object as PropType<IWorkflowTransitionActionData | null>,
      default: null
    },
    workflowId: {
      type: Number,
      required: true
    },
    transitionId: {
      type: Number,
      required: true
    }
  },
  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const store = useStore<IRootState>();

    const { useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const { getWorkflowTransitionActionTypeVisuals } = useGetters([
      'getWorkflowTransitionActionTypeVisuals'
    ]);

    const step = ref(WizardSteps.ActionBaseForm);

    const actionData = ref<IWorkflowTransitionActionData>({
      email_config: {
        recipients: []
      }
    });

    watch(localDialog, value => {
      if (value) {
        resetFormValidations();
        step.value = WizardSteps.ActionBaseForm;
        actionData.value = !props.isEdit
          ? {
              action_type: WorkflowTransitionActionTypeEnum.SendEmail,
              email_config: { recipients: [], subject: '', body: '' }
            }
          : cloneDeep(props.action!);

        includeOwner.value = recipientsListHasOwnerType.value;
      }
    });

    const actionBaseFormRef = ref<ALForm | null>(null);
    const actionTypeFormRef = ref<ALForm | null>(null);

    const isActionBaseFormValid = ref(false);
    const isActionTypeFormValid = ref(false);

    const includeOwner = ref(false);

    const subtitle = computed(() => {
      const actionText = props.isEdit ? 'Update' : 'Create';

      return step.value === WizardSteps.ActionTypeForm &&
        actionData.value.action_type ===
          WorkflowTransitionActionTypeEnum.SendEmail
        ? `Apply the 'Send Email' action information`
        : `${actionText} transition action`;
    });

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      updateAction,
      isUpdatingAction,
      updateActionError,
      updateActionErrorReset
    } = useUpdateAction(props.workflowId, props.transitionId);
    const {
      createAction,
      isCreatingAction,
      createActionError,
      createActionErrorReset
    } = useCreateAction(props.workflowId, props.transitionId);

    const localError = ref<ISystemError | null>(null);

    const isLoading = computed(() =>
      props.isEdit ? isCreatingAction.value : isUpdatingAction.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit
          ? updateActionError.value
          : createActionError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    const directEmailRecipients = computed({
      get() {
        const recipients =
          actionData.value.email_config &&
          actionData.value.email_config.recipients
            ? (actionData.value.email_config
                .recipients as IWorkflowTransitionActionEmailRecipientData[])
            : [];
        return recipients.filter(
          recipient =>
            recipient.recipient_subtype! ===
              TransitionActionRecipientSubTypeEnum.Direct &&
            recipient.recipient_type === TransitionActionRecipientTypeEnum.Email
        );
      },
      // if newValue has recipient that are not in the current list then add else remove them from the current list
      set(newRecipients) {
        const currentRecipients = actionData.value.email_config!
          .recipients as IWorkflowTransitionActionEmailRecipientData[];

        actionData.value.email_config!.recipients = updatedEmailRecipients(
          currentRecipients,
          newRecipients,
          TransitionActionRecipientSubTypeEnum.Direct
        );
      }
    });

    const ccEmailRecipients = computed({
      get() {
        const recipients =
          actionData.value.email_config &&
          actionData.value.email_config.recipients
            ? (actionData.value.email_config
                .recipients as IWorkflowTransitionActionEmailRecipientData[])
            : [];
        return recipients.filter(
          recipient =>
            recipient.recipient_subtype ===
              TransitionActionRecipientSubTypeEnum.CC &&
            recipient.recipient_type === TransitionActionRecipientTypeEnum.Email
        );
      },
      // if newValue has recipient that are not in the current list then add else remove them from the current list
      set(newRecipients) {
        const currentRecipients = actionData.value.email_config!
          .recipients as IWorkflowTransitionActionEmailRecipientData[];

        actionData.value.email_config!.recipients = updatedEmailRecipients(
          currentRecipients,
          newRecipients,
          TransitionActionRecipientSubTypeEnum.CC
        );
      }
    });

    function updatedEmailRecipients(
      currentRecipients: IWorkflowTransitionActionEmailRecipientData[],
      recipients: IWorkflowTransitionActionEmailRecipientData[],
      subtype: TransitionActionRecipientSubTypeEnum
    ) {
      const currentRecipientSet = new Set(currentRecipients);

      // Prepare a Set of new recipient emails for fast lookup
      const newValueEmailSet = new Set(recipients);

      const newRecipientsToAdd = recipients.filter(
        newRecipient => !currentRecipientSet.has(newRecipient)
      );

      const recipientsToRemove = currentRecipients.filter(
        recipient =>
          recipient.recipient_subtype === subtype &&
          !newValueEmailSet.has(recipient)
      );

      const recipientsToRemoveSet = new Set(recipientsToRemove);

      const updatedRecipients = currentRecipients.filter(
        recipient => !recipientsToRemoveSet.has(recipient)
      );

      return [...updatedRecipients, ...newRecipientsToAdd];
    }

    function clearErrors() {
      error.value = null;
      updateActionErrorReset();
      createActionErrorReset();
    }

    function resetFormValidations() {
      if (actionBaseFormRef.value) {
        actionBaseFormRef.value.resetValidation();
      }

      if (actionTypeFormRef.value) {
        actionTypeFormRef.value.resetValidation();
      }
    }

    function validateForm(formRef: ALForm): boolean {
      let valid = false;
      if (formRef) {
        valid = formRef.validate();
      }
      return valid;
    }

    async function saveBaseForm() {
      const valid = await validateForm(actionBaseFormRef.value!);

      if (valid) {
        if (
          actionData.value.action_type ===
          WorkflowTransitionActionTypeEnum.SendEmail
        ) {
          if (!actionData.value.email_config) {
            actionData.value.email_config = {
              recipients: []
            };
          }
          step.value = WizardSteps.ActionTypeForm;
        } else if (
          actionData.value.action_type ===
          WorkflowTransitionActionTypeEnum.GrantAccess
        ) {
          actionData.value.email_config = null;
          await saveTypeForm();
        }
      }
    }

    // if any recipient in the current list has owner type
    const recipientsListHasOwnerType = computed(() => {
      if (
        !actionData.value.email_config ||
        !actionData.value.email_config.recipients
      ) {
        return false;
      }
      return actionData.value.email_config.recipients.some(recipient =>
        typeof recipient === 'number'
          ? false
          : recipient.recipient_type === TransitionActionRecipientTypeEnum.Owner
      );
    });

    function configureEmailConfigData(
      transactionActionData: IWorkflowTransitionActionData
    ) {
      if (!recipientsListHasOwnerType.value && includeOwner.value) {
        transactionActionData.email_config!.recipients!.push({
          recipient_type: TransitionActionRecipientTypeEnum.Owner
        });
      } else if (!includeOwner.value) {
        transactionActionData.email_config!.recipients! = transactionActionData.email_config!.recipients!.filter(
          recipient =>
            typeof recipient === 'number'
              ? true
              : recipient.recipient_type ===
                TransitionActionRecipientTypeEnum.Email
        );
      }
    }

    function configureGrantAccessConfigData(
      transactionActionData: IWorkflowTransitionActionData
    ) {
      transactionActionData.email_config = null;
    }

    async function saveTypeForm() {
      const valid = await validateForm(actionTypeFormRef.value!);

      let transitionActionInstance: IWorkflowTransitionAction | null = null;
      const transactionActionData: IWorkflowTransitionActionData =
        actionData.value;

      switch (actionData.value.action_type) {
        case WorkflowTransitionActionTypeEnum.SendEmail: {
          if (!valid) {
            return;
          }
          configureEmailConfigData(transactionActionData);
          break;
        }
        case WorkflowTransitionActionTypeEnum.GrantAccess:
          configureGrantAccessConfigData(transactionActionData);
          break;
        default:
          throw new Error(
            `Invalid action type: ${actionData.value.action_type}`
          );
      }

      // Only mutate email_config data if the action type is not grant access else remove email config
      try {
        transitionActionInstance = new WorkflowTransitionAction(
          transactionActionData
        );
      } catch (e) {
        // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
        error.value = e as ISystemError;
      }

      if (transitionActionInstance != null) {
        const saveAction = props.isEdit ? updateAction : createAction;

        let payload = transitionActionInstance;
        if (props.isEdit) {
          payload = diffDeep(transitionActionInstance, props.action, true);
        }

        saveAction(payload, {
          onSuccess: () => {
            closeDialog();
          }
        });
      }
    }

    return {
      step,
      WorkflowTransitionActionTypeEnum,
      TransitionActionRecipientSubTypeEnum,
      localDialog,
      includeOwner,
      actionData,
      actionBaseFormRef,
      actionTypeFormRef,
      isActionBaseFormValid,
      isActionTypeFormValid,
      subtitle,
      isLoading,
      error,
      closeDialog,
      clearErrors,
      saveBaseForm,
      saveTypeForm,
      requiredValidator,

      WizardSteps,

      getWorkflowTransitionActionTypeVisuals,

      directEmailRecipients,
      ccEmailRecipients
    };
  }
});
